import { zodResolver } from "@hookform/resolvers/zod";
import {
	AlertCircle,
	ChevronDown,
	ChevronUp,
	Loader2,
	Plus,
	X,
} from "lucide-react";
import type React from "react";
import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import FieldError from "../components/FieldError";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import ValidationBanner from "../components/ValidationBanner";
import { useAuth } from "../context/AuthContext";
import { useWallets } from "../context/WalletContext";
import {
	ASSETS,
	CHAINS,
	CUSTODIAL_TYPES,
	MAU_RANGES,
	PRODUCTS,
	WISHLIST_STATUSES,
} from "../lib/constants";
import { type AddWalletFormData, addWalletSchema } from "../schemas/wallet";
import {
	type Asset,
	type Chain,
	COUNTRIES,
	type CustodialType,
	type IntegrationStatus,
	type MAURange,
	type OnboardingStatus,
	type Product,
	type Wallet,
	type WishlistStatus,
} from "../types";

const AddWallet: React.FC = () => {
	const navigate = useNavigate();
	const { addWallet } = useWallets();
	const { isAdmin } = useAuth();
	const bannerRef = useRef<HTMLDivElement>(null);

	const [showOptionalFields, setShowOptionalFields] = useState(false);
	const [marketSearch, setMarketSearch] = useState("");

	const form = useForm<AddWalletFormData>({
		resolver: zodResolver(addWalletSchema) as any,
		defaultValues: {
			walletName: "",
			companyName: "",
			selectedProducts: ["Withdraw"] as Product[],
			custodialType: "Custodial" as CustodialType,
			chain: "Stellar" as Chain,
			asset: "USDC" as Asset,
			contactName: "",
			contactEmail: "",
			contactPhone: "",
			contactRole: "",
			slackChannel: "",
			homeDomain: "",
			fundingAddress: "",
			conductsKYC: undefined as boolean | undefined,
			mau: undefined as MAURange | undefined,
			licenses: "",
			countryOfIncorporation: "",
			selectedWishlist: [] as WishlistStatus[],
			selectedMarkets: [] as string[],
			isExistingPartner: false,
			integrationStatus: "New - Pre-Integration",
			onboardingStatus: "Not Started",
		},
		mode: "onSubmit",
	});

	const isExisting = form.watch("isExistingPartner");
	const selectedProducts = form.watch("selectedProducts") ?? [];
	const selectedWishlist = form.watch("selectedWishlist") ?? [];
	const selectedMarkets = form.watch("selectedMarkets") ?? [];
	const errors = form.formState.errors;
	const hasErrors = Object.keys(errors).length > 0;
	const errorMessages = hasErrors
		? Object.values(errors)
				.filter((e): e is { message?: string } => !!e && typeof e === "object")
				.map((e) => e.message)
				.filter((m): m is string => !!m)
		: [];

	const handleProductToggle = (product: Product) => {
		const current = form.getValues("selectedProducts") ?? [];
		const next = current.includes(product)
			? current.filter((p) => p !== product)
			: [...current, product];
		form.setValue("selectedProducts", next);
	};

	const handleWishlistToggle = (status: WishlistStatus) => {
		const current = form.getValues("selectedWishlist") ?? [];
		const next = current.includes(status)
			? current.filter((s) => s !== status)
			: [...current, status];
		form.setValue("selectedWishlist", next);
	};

	const handleMarketSelect = (country: string) => {
		const current = form.getValues("selectedMarkets") ?? [];
		if (!current.includes(country)) {
			form.setValue("selectedMarkets", [...current, country]);
		}
		setMarketSearch("");
	};

	const handleMarketRemove = (country: string) => {
		const current = form.getValues("selectedMarkets") ?? [];
		form.setValue(
			"selectedMarkets",
			current.filter((c) => c !== country),
		);
	};

	const handleSelectAllMarkets = () => {
		form.setValue("selectedMarkets", COUNTRIES);
	};

	const filteredCountries = COUNTRIES.filter(
		(country) =>
			country.toLowerCase().includes(marketSearch.toLowerCase()) &&
			!selectedMarkets.includes(country),
	);

	const [isCreating, setIsCreating] = useState(false);

	const onValidSubmit = async (data: AddWalletFormData) => {
		const existing = !!data.isExistingPartner;
		const products =
			(data.selectedProducts?.length ?? 0) > 0
				? data.selectedProducts
				: (["Withdraw"] as Product[]);

		const newWallet: Wallet = {
			id: crypto.randomUUID(),
			wallet_name: data.walletName,
			company_name: data.companyName,
			custodial_type: (data.custodialType as CustodialType) ?? "Custodial",
			chain: (data.chain as Chain) ?? "Stellar",
			asset: (data.asset as Asset) ?? "USDC",
			products,
			main_contact_name: data.contactName ?? "",
			main_contact_email: data.contactEmail ?? "",
			main_contact_phone: data.contactPhone ?? "",
			main_contact_role: data.contactRole ?? "",
			slack_channel: data.slackChannel ?? "",
			home_domain: data.homeDomain ?? "",
			funding_address: data.fundingAddress ?? "",
			conducts_kyc:
				data.conductsKYC != null ? Boolean(data.conductsKYC) : undefined,
			mau: (data.mau || undefined) as MAURange | undefined,
			country_of_incorporation: data.countryOfIncorporation ?? "",
			priority_markets:
				(data.selectedMarkets?.length ?? 0) > 0
					? data.selectedMarkets
					: undefined,
			integration_status: existing
				? ((data.integrationStatus ??
						"New - Pre-Integration") as IntegrationStatus)
				: "New - Pre-Integration",
			onboarding_status: existing
				? ((data.onboardingStatus ?? "Not Started") as OnboardingStatus)
				: "Not Started",
			is_existing_partner: existing || undefined,
			is_whitelisted: existing
				? (data.selectedWishlist?.length ?? 0) > 0
				: false,
			whitelist_environments: existing
				? (data.selectedWishlist as WishlistStatus[])
				: undefined,
			created_at: new Date().toISOString(),
			updated_at: new Date().toISOString(),
		};

		setIsCreating(true);
		try {
			const createdWallet = await addWallet(newWallet);
			navigate(`/wallet/${createdWallet.id}`);
		} catch (err) {
			console.error("Failed to create wallet:", err);
			setIsCreating(false);
		}
	};

	const onInvalid = () => {
		bannerRef.current?.scrollIntoView({ behavior: "smooth" });
	};

	return (
		<div className="min-h-[calc(100vh-56px)] overflow-x-hidden bg-(--color-background) px-4 pb-8 pt-20">
			<div className="mx-auto max-w-[1000px]">
				<div className="mb-6">
					<h1 className="mb-2 text-3xl font-bold text-(--color-foreground)">
						Add New Wallet
					</h1>
					<p className="text-(--color-muted-foreground) text-base">
						Register a new wallet partner to track their integration and
						onboarding journey
					</p>
				</div>

				<form
					onSubmit={form.handleSubmit(onValidSubmit as any, onInvalid)}
					className="flex flex-col gap-6"
				>
					<div ref={bannerRef}>
						{hasErrors && (
							<ValidationBanner
								message="Please fix the errors below before submitting."
								fields={errorMessages}
								aria-live="polite"
							/>
						)}
					</div>

					{/* Existing Partner Toggle — Admin Only */}
					{isAdmin && (
						<Card className="border-2 border-[#F59E0B]/40 dark:border-[#F59E0B]/30 bg-gradient-to-br from-[#F59E0B]/5 to-[#F59E0B]/10 dark:from-[#F59E0B]/10 dark:to-[#F59E0B]/5 p-5 rounded-xl">
							<div className="flex items-center justify-between gap-5">
								<div className="flex flex-1 items-start gap-4">
									<AlertCircle
										size={20}
										className="mt-0.5 shrink-0 text-[#F59E0B]"
									/>
									<div>
										<h3 className="mb-1 font-semibold text-(--color-foreground)">
											Existing Partner
										</h3>
										<p className="text-(--color-muted-foreground) text-sm leading-relaxed">
											Enable for partners onboarded before the portal. Set their
											current status and fill in details later.
										</p>
									</div>
								</div>
								<Switch
									checked={!!isExisting}
									onCheckedChange={(checked) =>
										form.setValue("isExistingPartner", checked)
									}
								/>
							</div>

							{isExisting && (
								<div className="mt-5 border-t-2 border-[#F59E0B]/30 pt-5">
									<div className="grid grid-cols-1 gap-4 md:grid-cols-3">
										<div className="space-y-2">
											<Label
												htmlFor="integrationStatus"
												className="font-semibold text-(--color-foreground) text-sm"
											>
												Integration Status
											</Label>
											<select
												id="integrationStatus"
												{...form.register("integrationStatus")}
												className="flex h-10 w-full rounded-xl border border-(--color-border) bg-(--color-card) px-3 py-2 text-sm text-(--color-foreground) shadow-sm focus:outline-none focus:ring-2 focus:ring-(--color-accent) transition-all"
											>
												<option value="New - Pre-Integration">
													New - Pre-Integration
												</option>
												<option value="Integrating">Integrating</option>
												<option value="Testing">Testing</option>
												<option value="Ready to Go Live">
													Ready to Go Live
												</option>
												<option value="Live">Live</option>
												<option value="On-Hold">On-Hold</option>
												<option value="Abandoned">Abandoned</option>
											</select>
										</div>

										<div className="space-y-2">
											<Label
												htmlFor="onboardingStatus"
												className="font-semibold text-(--color-foreground) text-sm"
											>
												Onboarding Status
											</Label>
											<select
												id="onboardingStatus"
												{...form.register("onboardingStatus")}
												className="flex h-10 w-full rounded-xl border border-(--color-border) bg-(--color-card) px-3 py-2 text-sm text-(--color-foreground) shadow-sm focus:outline-none focus:ring-2 focus:ring-(--color-accent) transition-all"
											>
												<option value="Not Started">Not Started</option>
												<option value="Onboarding in Progress">
													Onboarding in Progress
												</option>
												<option value="Onboarded">Onboarded</option>
											</select>
										</div>

										<div className="space-y-2">
											<Label
												htmlFor="isWhitelisted"
												className="font-semibold text-(--color-foreground) text-sm"
											>
												Whitelisted?
											</Label>
											<select
												id="isWhitelisted"
												value={
													(form.watch("selectedWishlist")?.length ?? 0) > 0
														? "true"
														: "false"
												}
												onChange={(e) => {
													if (e.target.value === "true") {
														form.setValue("selectedWishlist", [
															"Sandbox",
															"Preview",
															"Production",
														] as WishlistStatus[]);
													} else {
														form.setValue("selectedWishlist", []);
													}
												}}
												className="flex h-10 w-full rounded-xl border border-(--color-border) bg-(--color-card) px-3 py-2 text-sm text-(--color-foreground) shadow-sm focus:outline-none focus:ring-2 focus:ring-(--color-accent) transition-all"
											>
												<option value="false">No</option>
												<option value="true">Yes</option>
											</select>
										</div>
									</div>
								</div>
							)}
						</Card>
					)}

					{/* Basic Information */}
					<Card className="p-5 rounded-xl bg-(--color-card) border-(--color-border)">
						<h2 className="mb-4 font-semibold text-lg text-(--color-foreground)">
							Basic Information
						</h2>
						<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
							<div className="space-y-2">
								<Label
									htmlFor="walletName"
									className="text-(--color-foreground)"
								>
									Wallet Name <span className="text-(--color-accent)">*</span>
								</Label>
								<Input
									type="text"
									id="walletName"
									{...form.register("walletName")}
									className={
										errors.walletName
											? "border-(--color-accent) rounded-xl"
											: "rounded-xl border-(--color-border)"
									}
									aria-invalid={!!errors.walletName}
									aria-describedby={
										errors.walletName ? "walletName-error" : undefined
									}
								/>
								{errors.walletName?.message && (
									<FieldError
										id="walletName-error"
										message={String(errors.walletName.message)}
									/>
								)}
							</div>

							<div className="space-y-2">
								<Label
									htmlFor="companyName"
									className="text-(--color-foreground)"
								>
									Company Name <span className="text-(--color-accent)">*</span>
								</Label>
								<Input
									type="text"
									id="companyName"
									{...form.register("companyName")}
									className={
										errors.companyName
											? "border-(--color-accent) rounded-xl"
											: "rounded-xl border-(--color-border)"
									}
									aria-invalid={!!errors.companyName}
									aria-describedby={
										errors.companyName ? "companyName-error" : undefined
									}
								/>
								{errors.companyName?.message && (
									<FieldError
										id="companyName-error"
										message={String(errors.companyName.message)}
									/>
								)}
							</div>

							<div className="space-y-2">
								<Label
									htmlFor="custodialType"
									className="text-(--color-foreground)"
								>
									Custodial Type{" "}
									<span className="text-(--color-accent)">*</span>
								</Label>
								<select
									id="custodialType"
									{...form.register("custodialType")}
									className="flex h-10 w-full rounded-xl border border-(--color-border) bg-(--color-card) px-3 py-2 text-sm text-(--color-foreground) shadow-sm focus:outline-none focus:ring-2 focus:ring-(--color-accent) transition-all"
								>
									{CUSTODIAL_TYPES.map((t) => (
										<option key={t} value={t}>
											{t}
										</option>
									))}
								</select>
							</div>

							<div className="space-y-2">
								<Label htmlFor="chain" className="text-(--color-foreground)">
									Chain <span className="text-(--color-accent)">*</span>
								</Label>
								<select
									id="chain"
									{...form.register("chain")}
									className="flex h-10 w-full rounded-xl border border-(--color-border) bg-(--color-card) px-3 py-2 text-sm text-(--color-foreground) shadow-sm focus:outline-none focus:ring-2 focus:ring-(--color-accent) transition-all"
								>
									{CHAINS.map((c) => (
										<option key={c} value={c}>
											{c}
										</option>
									))}
								</select>
							</div>

							<div className="space-y-2">
								<Label htmlFor="asset" className="text-(--color-foreground)">
									Asset <span className="text-(--color-accent)">*</span>
								</Label>
								<select
									id="asset"
									{...form.register("asset")}
									className="flex h-10 w-full rounded-xl border border-(--color-border) bg-(--color-card) px-3 py-2 text-sm text-(--color-foreground) shadow-sm focus:outline-none focus:ring-2 focus:ring-(--color-accent) transition-all"
								>
									{ASSETS.map((a) => (
										<option key={a} value={a}>
											{a}
										</option>
									))}
								</select>
							</div>

							<div className="col-span-full space-y-2">
								{/* biome-ignore lint/a11y/noLabelWithoutControl: Checkbox group label */}
								<Label className="text-(--color-foreground)">
									Products{" "}
									{isExisting ? (
										<span className="font-normal text-(--color-muted-foreground)">
											(Optional for existing partners)
										</span>
									) : (
										<span className="text-(--color-accent)">*</span>
									)}
								</Label>
								<div className="flex flex-wrap gap-4">
									{PRODUCTS.map((product) => (
										<label
											key={product}
											className="flex cursor-pointer items-center gap-2.5"
										>
											<input
												type="checkbox"
												checked={selectedProducts.includes(product)}
												onChange={() => handleProductToggle(product)}
												className="h-4 w-4 rounded border-(--color-border) accent-(--color-accent)"
											/>
											<span className="font-medium text-sm text-(--color-foreground)">
												{product}
											</span>
										</label>
									))}
								</div>
								{errors.selectedProducts?.message && (
									<FieldError
										message={String(errors.selectedProducts.message)}
									/>
								)}
							</div>
						</div>
					</Card>

					{/* Main Contact */}
					<Card className="p-5 rounded-xl bg-(--color-card) border-(--color-border)">
						<h2 className="mb-4 font-semibold text-lg text-(--color-foreground)">
							Main Contact
						</h2>
						<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
							<div className="space-y-2">
								<Label
									htmlFor="contactName"
									className="text-(--color-foreground)"
								>
									Name
								</Label>
								<Input
									type="text"
									id="contactName"
									{...form.register("contactName")}
									className="rounded-xl border-(--color-border)"
								/>
							</div>

							<div className="space-y-2">
								<Label
									htmlFor="contactEmail"
									className="text-(--color-foreground)"
								>
									Email
								</Label>
								<Input
									type="email"
									id="contactEmail"
									{...form.register("contactEmail")}
									className="rounded-xl border-(--color-border)"
								/>
							</div>

							<div className="space-y-2">
								<Label
									htmlFor="contactPhone"
									className="text-(--color-foreground)"
								>
									Phone Number
								</Label>
								<Input
									type="tel"
									id="contactPhone"
									{...form.register("contactPhone")}
									className="rounded-xl border-(--color-border)"
								/>
							</div>

							<div className="space-y-2">
								<Label
									htmlFor="contactRole"
									className="text-(--color-foreground)"
								>
									Role
								</Label>
								<Input
									type="text"
									id="contactRole"
									{...form.register("contactRole")}
									placeholder="Optional"
									className="rounded-xl border-(--color-border)"
								/>
							</div>
						</div>
					</Card>

					{/* Optional Fields */}
					<Card className="p-5 rounded-xl bg-(--color-card) border-(--color-border)">
						<button
							type="button"
							className="mb-4 flex w-full items-center justify-between rounded-xl bg-(--color-muted) px-4 py-3 font-semibold text-(--color-foreground) text-sm uppercase tracking-wide transition-all hover:bg-(--color-border)"
							onClick={() => setShowOptionalFields(!showOptionalFields)}
						>
							<span>Optional Fields</span>
							{showOptionalFields ? (
								<ChevronUp
									size={20}
									className="text-(--color-muted-foreground)"
								/>
							) : (
								<ChevronDown
									size={20}
									className="text-(--color-muted-foreground)"
								/>
							)}
						</button>

						{showOptionalFields && (
							<div className="space-y-5">
								{/* Technical Info */}
								<div>
									<h3 className="mb-3 font-semibold text-(--color-foreground) text-sm uppercase tracking-wide">
										Technical Information
									</h3>
									<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
										<div className="space-y-2">
											<Label
												htmlFor="slackChannel"
												className="text-(--color-foreground)"
											>
												Slack Channel
											</Label>
											<Input
												type="text"
												id="slackChannel"
												{...form.register("slackChannel")}
												placeholder="e.g., #wallet-integration"
												className="rounded-xl border-(--color-border)"
											/>
										</div>

										<div className="space-y-2">
											<Label
												htmlFor="homeDomain"
												className="text-(--color-foreground)"
											>
												Home Domain
											</Label>
											<Input
												type="text"
												id="homeDomain"
												{...form.register("homeDomain")}
												placeholder="e.g., wallet.example.com"
												className="rounded-xl border-(--color-border)"
											/>
										</div>

										<div className="col-span-full space-y-2">
											<Label
												htmlFor="fundingAddress"
												className="text-(--color-foreground)"
											>
												Funding Address
											</Label>
											<Input
												type="text"
												id="fundingAddress"
												{...form.register("fundingAddress")}
												placeholder="Enter blockchain funding address"
												className="rounded-xl border-(--color-border)"
											/>
										</div>
									</div>
								</div>

								{/* Whitelist Status */}
								<div>
									<h3 className="mb-3 font-semibold text-(--color-foreground) text-sm uppercase tracking-wide">
										Whitelist Status
									</h3>
									<div className="flex flex-wrap gap-4">
										{WISHLIST_STATUSES.map((status) => (
											<label
												key={status}
												className="flex cursor-pointer items-center gap-2.5"
											>
												<input
													type="checkbox"
													checked={selectedWishlist.includes(status)}
													onChange={() => handleWishlistToggle(status)}
													className="h-4 w-4 rounded border-(--color-border) accent-(--color-accent)"
												/>
												<span className="font-medium text-sm text-(--color-foreground)">
													{status}
												</span>
											</label>
										))}
									</div>
								</div>

								{/* Additional details */}
								<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
									<div className="space-y-2">
										<Label
											htmlFor="conductsKYC"
											className="text-(--color-foreground)"
										>
											Does the wallet conduct KYC on users?
										</Label>
										<select
											id="conductsKYC"
											{...form.register("conductsKYC")}
											className="flex h-10 w-full rounded-xl border border-(--color-border) bg-(--color-card) px-3 py-2 text-sm text-(--color-foreground) shadow-sm focus:outline-none focus:ring-2 focus:ring-(--color-accent) transition-all"
										>
											<option value="">Select...</option>
											<option value="true">Yes</option>
											<option value="false">No</option>
										</select>
									</div>

									<div className="space-y-2">
										<Label htmlFor="mau" className="text-(--color-foreground)">
											Monthly Active Users (MAU)
										</Label>
										<select
											id="mau"
											{...form.register("mau")}
											className="flex h-10 w-full rounded-xl border border-(--color-border) bg-(--color-card) px-3 py-2 text-sm text-(--color-foreground) shadow-sm focus:outline-none focus:ring-2 focus:ring-(--color-accent) transition-all"
										>
											<option value="">Select range...</option>
											{MAU_RANGES.map((r) => (
												<option key={r} value={r}>
													{r}
												</option>
											))}
										</select>
									</div>

									<div className="col-span-full space-y-2">
										<Label
											htmlFor="licenses"
											className="text-(--color-foreground)"
										>
											Licenses
										</Label>
										<Textarea
											id="licenses"
											{...form.register("licenses")}
											rows={3}
											placeholder="Enter any relevant licenses..."
											className="rounded-xl border-(--color-border)"
										/>
									</div>

									<div className="space-y-2">
										<Label
											htmlFor="countryOfIncorporation"
											className="text-(--color-foreground)"
										>
											Country of Incorporation
										</Label>
										<select
											id="countryOfIncorporation"
											{...form.register("countryOfIncorporation")}
											className="flex h-10 w-full rounded-xl border border-(--color-border) bg-(--color-card) px-3 py-2 text-sm text-(--color-foreground) shadow-sm focus:outline-none focus:ring-2 focus:ring-(--color-accent) transition-all"
										>
											<option value="">Select country...</option>
											{COUNTRIES.map((country) => (
												<option key={country} value={country}>
													{country}
												</option>
											))}
										</select>
									</div>

									<div className="col-span-full space-y-2">
										<Label
											htmlFor="market-search"
											className="text-(--color-foreground)"
										>
											Priority Markets Served
										</Label>
										<div className="relative">
											<div className="mb-3 flex gap-3">
												<Input
													id="market-search"
													type="text"
													placeholder="Search countries..."
													value={marketSearch}
													onChange={(e) => setMarketSearch(e.target.value)}
													className="flex-1 rounded-xl border-(--color-border)"
												/>
												<Button
													type="button"
													variant="outline"
													size="sm"
													onClick={handleSelectAllMarkets}
													className="rounded-xl border-(--color-border) hover:bg-(--color-muted)"
												>
													Select All
												</Button>
											</div>

											{marketSearch && filteredCountries.length > 0 && (
												<div className="absolute top-[calc(100%-0.75rem)] right-0 left-0 z-[100] max-h-52 overflow-y-auto rounded-xl border border-(--color-border) bg-(--color-background) shadow-lg">
													{filteredCountries.slice(0, 10).map((country) => (
														// biome-ignore lint/a11y/useSemanticElements: Dropdown option needs to be a div for styling
														<div
															key={country}
															className="cursor-pointer px-4 py-3 text-sm text-(--color-foreground) transition-colors hover:bg-(--color-muted)"
															role="button"
															tabIndex={0}
															onClick={() => handleMarketSelect(country)}
															onKeyDown={(e) => {
																if (e.key === "Enter" || e.key === " ") {
																	e.preventDefault();
																	handleMarketSelect(country);
																}
															}}
														>
															{country}
														</div>
													))}
												</div>
											)}

											{selectedMarkets.length > 0 && (
												<div className="flex min-h-[60px] flex-wrap gap-2 rounded-xl bg-(--color-muted) p-3">
													{selectedMarkets.map((country) => (
														<span
															key={country}
															className="inline-flex items-center gap-1.5 rounded-full border border-(--color-border) bg-(--color-card) px-3 py-1.5 font-medium text-sm text-(--color-foreground)"
														>
															{country}
															<button
																type="button"
																onClick={() => handleMarketRemove(country)}
																className="flex items-center justify-center rounded-full p-0.5 text-(--color-muted-foreground) transition-colors hover:bg-(--color-accent) hover:text-white"
															>
																<X size={14} />
															</button>
														</span>
													))}
												</div>
											)}
										</div>
									</div>
								</div>
							</div>
						)}
					</Card>

					<div className="flex justify-end gap-3 pt-2">
						<Button
							type="button"
							variant="outline"
							onClick={() => navigate("/")}
							className="rounded-xl border-(--color-border) hover:bg-(--color-muted)"
						>
							Cancel
						</Button>
						<Button
							type="submit"
							disabled={isCreating}
							className="rounded-xl bg-(--color-cta-primary) hover:bg-(--color-cta-primary-hover) text-white"
						>
							{isCreating ? (
								<>
									<Loader2 size={20} className="animate-spin" />
									Creating...
								</>
							) : (
								<>
									<Plus size={20} />
									Add Wallet
								</>
							)}
						</Button>
					</div>
				</form>
			</div>
		</div>
	);
};

export default AddWallet;
